import { canonicalizeBookingStatus } from './bookingService';
import { isProposalContractComplete, toClientWorkflowStage, normalizeWorkflowValue } from './proposalContract';

const LIVE_EVENT_ELIGIBLE_VALUES = new Set(['accepted', 'event_preparation', 'event_live']);

export { normalizeWorkflowValue };

/** Canonical LIVE marker for dashboard eligibility (project_stage first). */
export const isBookingEventLive = (booking) => {
  if (normalizeWorkflowValue(booking?.project_stage) === 'event_live') return true;
  if (normalizeWorkflowValue(booking?.status) === 'event_live') return true;
  if (normalizeWorkflowValue(booking?.booking_status) === 'event_live') return true;
  return false;
};

export const isCorporateBooking = (booking) => (
  normalizeWorkflowValue(booking?.booking_type) === 'corporate'
);

export const isLiveEventEligibleBooking = (booking) => {
  const normalizedStatus = canonicalizeBookingStatus(booking?.status || booking?.booking_status);
  const projectStage = normalizeWorkflowValue(booking?.project_stage);
  const bookingStage = normalizeWorkflowValue(booking?.booking_status);

  if (!isProposalContractComplete(booking)) return false;

  return (
    LIVE_EVENT_ELIGIBLE_VALUES.has(normalizedStatus)
    || LIVE_EVENT_ELIGIBLE_VALUES.has(projectStage)
    || LIVE_EVENT_ELIGIBLE_VALUES.has(bookingStage)
  );
};

export const resolveBookingWorkflowStage = (
  booking,
  {
    statusToStage = {},
    fallbackStage = 'pending_review',
    isKnownStage = () => false,
  } = {}
) => {
  const status = toClientWorkflowStage(canonicalizeBookingStatus(booking?.status || booking?.booking_status));
  const pacComplete = isProposalContractComplete(booking);

  const stageCandidates = [booking?.project_stage, booking?.booking_status, booking?.status]
    .map((candidate) => toClientWorkflowStage(candidate))
    .filter(Boolean);

  if (status === 'declined' || stageCandidates.includes('declined')) {
    return 'declined';
  }

  if (stageCandidates.includes('event_live') && pacComplete) {
    return 'event_live';
  }

  if (stageCandidates.includes('event_preparation') && pacComplete) {
    return 'event_preparation';
  }

  if (status === 'event_live' && pacComplete) {
    return 'event_live';
  }

  if ((status === 'event_preparation' || status === 'accepted') && pacComplete) {
    return 'event_preparation';
  }

  const inPacOrLater = ['proposal_contract', 'event_preparation', 'event_live'].some(
    (key) => status === key || stageCandidates.includes(key)
  );
  if (inPacOrLater) {
    return 'proposal_contract';
  }

  for (const candidate of stageCandidates) {
    if (candidate && isKnownStage(candidate) && candidate !== 'event_preparation' && candidate !== 'event_live') {
      return candidate;
    }
  }

  return toClientWorkflowStage(statusToStage[status] || statusToStage[canonicalizeBookingStatus(booking?.status)] || fallbackStage);
};
